import * as Yup from 'yup';
import { debounce } from 'lodash';
import { useEffect } from 'react';
import { useSnackbar } from 'notistack';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import Iconify from '@emfood/shared/src/components/iconify';
import {
  RHFSelect,
  RHFCheckbox,
  FormProvider,
  RHFTextField,
  RHFAutocompleteSelect,
  RHFProductAutocompleteSelect,
} from '@emfood/shared';

import { LoadingButton } from '@mui/lab';
import {
  Stack,
  Button,
  Divider,
  MenuItem,
  Typography,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
} from '@mui/material';

import { useProductsList } from 'src/hooks/use-products.list';

import { RHFUpload } from 'src/components/hook-form/rhf-upload';
import { ProductIssueType } from 'src/api/models/orders';
import { useCreateOrderIssue, useUploadOrderIssueImage } from 'src/api/hooks/api-hooks';

type ReportDifferentProductFormProps = {
  orderId: string;
  productId: string;
  productName: string;
  productCost: string;
  productQuantity: number;
  issueType: ProductIssueType | null;
  issueTypeDescription: string;
  goBack: (isDirty: boolean) => void;
  onSuccess: () => void;
  onCancel: (isDirty: boolean) => void;
};

const MAX_PHOTO_SIZE_BYTES = 5 * 1024 * 1024;

const validatePhotoSize = (file: File) =>
  file.size > MAX_PHOTO_SIZE_BYTES
    ? { code: 'file-too-large', message: 'This photo is larger than 5MB' }
    : null;

const ReportDifferentProductSchema = Yup.object().shape({
  productStockId: Yup.string()
    .label('Product')
    .when('isCustomProduct', {
      is: false,
      then: (schema) => schema.required('Product is required'),
    }),
  productPriceId: Yup.string()
    .label('Product Price')
    .when('isCustomProduct', {
      is: false,
      then: (schema) => schema.required('Product Price is required'),
    }),
  productDetails: Yup.string()
    .label('Product Details')
    .when('isCustomProduct', {
      is: true,
      then: (schema) => schema.required('Product Details is required'),
    }),
  productPrice: Yup.string()
    .label('Product Price')
    .when('isCustomProduct', {
      is: true,
      then: (schema) => schema.required('Product Price is required'),
    }),
  productQuantity: Yup.number().label('Product Quantity').typeError('Please enter a number'),
  isCustomProduct: Yup.boolean().label('Custom Product').required(),
  resolutionType: Yup.string().label('Resolution Type').required(),
  reason: Yup.string().label('Reason').required(),
  comments: Yup.string().label('Comments'),
  photos: Yup.array().of(Yup.mixed<File>().required()).max(5, 'You can attach up to 5 photos'),
});

const ReportDifferentProductForm = ({
  orderId,
  productId,
  productName,
  productCost,
  productQuantity,
  issueType,
  issueTypeDescription,
  goBack,
  onSuccess,
  onCancel,
}: ReportDifferentProductFormProps) => {
  const { products, setFilterSearchModel } = useProductsList();
  const defaultValues = {
    productStockId: '',
    reason: '',
    productDetails: '',
    productPriceId: '',
    productQuantity: 1,
    productPrice: '',
    isCustomProduct: false,
    comments: '',
    resolutionType: '',
    photos: [] as File[],
  };

  const { mutateAsync: createOrderIssue } = useCreateOrderIssue();
  const { mutateAsync: uploadOrderIssueImage } = useUploadOrderIssueImage();

  const methods = useForm({
    resolver: yupResolver(ReportDifferentProductSchema),
    defaultValues,
  });

  const {
    handleSubmit,
    formState: { isSubmitting, isDirty },
    setValue,
    reset,
  } = methods;

  const { enqueueSnackbar } = useSnackbar();

  const handleFormSubmit = handleSubmit(async (data) => {
    if (issueType) {
      const substitutionInfo = data.isCustomProduct
        ? {
            productName: data.productDetails,
            productPrice: Number(data.productPrice),
            productQuantity: data.productQuantity,
            reasonForChange: Number(data.reason),
          }
        : {
            productStockId: data.productStockId,
            productPriceId: data.productPriceId,
            productQuantity: data.productQuantity,
            reasonForChange: Number(data.reason),
          };

      try {
        const imageUrls = await Promise.all(
          (data.photos ?? []).map(async (file) => {
            const fileFormData = new FormData();
            fileFormData.append('file-data', file);
            const uploadResult = await uploadOrderIssueImage({
              body: fileFormData,
              pathParams: { orderId },
            });
            return uploadResult.result;
          })
        );

        const submitData = {
          buyerOrderProductId: productId,
          issueType,
          details: data.comments,
          resolutionType: Number(data.resolutionType),
          substitutionInfo,
          images: imageUrls,
        };
        await createOrderIssue({ body: submitData, pathParams: { id: orderId } });

        onSuccess();
        reset();
      } catch {
        enqueueSnackbar('Your issue was not submitted. Please try again.', { variant: 'error' });
      }
    }
  });

  const productSearch = debounce((value: string) => {
    setFilterSearchModel({ productName: value });
  }, 500);

  const isCustomProduct = methods.watch('isCustomProduct');

  // Reset productStockId and productPriceId when the user selects the custom product option
  useEffect(() => {
    if (isCustomProduct) {
      setValue('productStockId', '');
      setValue('productPriceId', '');
    }
  }, [isCustomProduct, setValue]);

  return (
    <FormProvider methods={methods} onSubmit={handleFormSubmit}>
      <DialogTitle>
        <Button
          variant="outlined"
          color="inherit"
          onClick={() => {
            goBack(isDirty);
          }}
          startIcon={<Iconify icon="mdi:chevron-left" />}
          sx={{ mb: 2 }}
        >
          Back
        </Button>
        <div>
          <Typography variant="h6">Different Product</Typography>
          <Typography variant="subtitle1">{issueTypeDescription}</Typography>
        </div>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1" sx={{ mb: 3 }}>
          You ordered {productQuantity} unit{productQuantity > 1 ? 's' : ''} of {productName} which
          should have cost £{productCost}.
        </Typography>
        <Stack sx={{ mt: 2 }} spacing={4}>
          <Stack spacing={2}>
            <Typography>Select Substitute Product</Typography>
            <RHFProductAutocompleteSelect
              name="productStockId"
              label="Select the product it was replaced with"
              fullWidth
              options={products.map((product) => ({
                id: product.productStockId,
                name: product.productTemplate.name,
                units: product.units,
                priceId: product.productPriceId,
              }))}
              onInputChange={(_, value, reason) => {
                if (reason === 'input') {
                  productSearch(value);
                }
              }}
              // overriden filterOptions because we're doing filtering server-side
              filterOptions={(x) => x}
              onValueChange={(value) => {
                const newProductPriceId = products.find(
                  (product) => product.productStockId === value
                )?.productPriceId;
                setValue('productPriceId', newProductPriceId);
              }}
              placeholder="Search for the substitute product"
              disabled={isCustomProduct}
            />

            <RHFCheckbox
              name="isCustomProduct"
              label="I can't find the product in the search box"
            />
            {isCustomProduct && (
              <>
                <RHFTextField
                  name="productDetails"
                  label="Add the name of the product you received"
                  fullWidth
                />
                <RHFTextField
                  name="productPrice"
                  label="Add the actual price of the product you received"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">£</InputAdornment>,
                  }}
                />
              </>
            )}
            <RHFTextField
              name="productQuantity"
              label="Add the quantity of the product you received"
              inputProps={{
                inputMode: 'numeric',
                pattern: '[0-9]*',
              }}
            />
          </Stack>
          <Divider />
          <Stack spacing={2}>
            <Typography>Issue Details</Typography>
            <RHFSelect
              name="reason"
              label="What is the reason for the different product?"
              fullWidth
            >
              <MenuItem value={1}>Substitution</MenuItem>
              <MenuItem value={2}>Supplier Error</MenuItem>
              <MenuItem value={3}>Unknown</MenuItem>
            </RHFSelect>
            <RHFTextField name="comments" label="Comments" fullWidth multiline rows={4} />
            <RHFSelect
              name="resolutionType"
              label="What resolution would you like to receive?"
              fullWidth
              disabled={!issueType}
            >
              <MenuItem value={1}>Refund</MenuItem>
              <MenuItem value={2}>Replace</MenuItem>
              <MenuItem value={3}>No Action</MenuItem>
            </RHFSelect>
          </Stack>
          <Stack spacing={1}>
            <Typography variant="subtitle2">Add photos (optional)</Typography>
            <RHFUpload
              name="photos"
              multiple
              thumbnail
              accept={{ 'image/*': ['.jpeg', '.jpg', '.png'] }}
              validator={validatePhotoSize}
              helperText="Up to 5 photos, 5MB each. Include a photo of the product label showing the best-before date and batch number — the supplier needs this to investigate."
            />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onCancel(isDirty)}>Cancel</Button>
        {issueType && (
          <LoadingButton
            loading={isSubmitting}
            onClick={handleFormSubmit}
            variant="contained"
            disabled={isSubmitting || !isDirty}
          >
            Save Changes
          </LoadingButton>
        )}
      </DialogActions>
    </FormProvider>
  );
};

export default ReportDifferentProductForm;
